import '../../store/types'
import { List, Map } from 'immutable'
import { getAuthIsLoggedIn } from '../auth/index'
import {
  filterEntitlementsByContentSetAndSku,
  filterEcommerceSkuEntitlements,
} from './entitlements'

/**
 * @param {ReduxState} state redux state
 * @returns {MapConstructor}
 */
export function selectUserData (state) {
  return state.user.get('data', Map())
}

/**
 * @param {ReduxState} state redux state
 * @returns {String}
 */
export function selectName (state) {
  return state.user.getIn(['data', 'name'], '')
}

/**
 * @param {ReduxState} state redux state
 * @returns {String}
 */
export function selectFirstName (state) {
  return state.user.getIn(['data', 'firstName'], '')
}

/**
 * @param {ReduxState} state redux state
 * @returns {String}
 */
export function selectLastName (state) {
  return state.user.getIn(['data', 'lastName'], '')
}

/**
 * @param {ReduxState} state redux state
 * @returns {String}
 */
export function selectEmail (state) {
  return state.user.getIn(['data', 'mail'], '')
}

/**
 * @param {ReduxState} state redux state
 * @returns {String}
 */
export function selectPrimaryLanguage (state) {
  const languages = state.user.getIn(['data', 'language'], undefined)
  return List.isList(languages) ? languages.first() : undefined
}

/**
 * @param {ReduxState} state redux state
 * @returns {Bool}
 */
export function selectEmailAvailabilityProcessing (state) {
  return state.user.get('emailAvailabilityProcessing', undefined)
}

/**
 * @param {ReduxState} state redux state
 * @returns {Bool}
 */
export function selectEmailAvailability (state) {
  return state.user.get('emailAvailable', null)
}

/**
 * @param {ReduxState} state redux state
 * @returns {Bool}
 */
export function selectHasEmailAvailable (state) {
  return state.user.has('emailAvailable')
}

/**
 * @param {ReduxState} state redux state
 * @returns {Bool}
 */
export function selectHasChildProfile (state) {
  return state.user.has('childProfile')
}

/**
 * @param {ReduxState} state redux state
 * @returns {Bool}
 */
export function selectIsChildProfile (state) {
  return state.user.get('childProfile') === true
}

/**
 * @param {ReduxState} state redux state
 * @returns {String}
 */
export function selectUserExternalId (state) {
  const { auth = Map(), user = Map() } = state
  const isLoggedIn = getAuthIsLoggedIn(auth)
  const externalId = !isLoggedIn ? user.getIn(['data', 'anonymousUuid'], '') : user.getIn(['data', 'uuid'], '') || user.getIn(['data', 'anonymousUuid'], '')

  return externalId
}

/**
 * @param {ReduxState} state redux state
 * @returns {String}
 */
export function selectUserFullName (state) {
  const firstName = state.user.getIn(['data', 'firstName'], '')
  const lastName = state.user.getIn(['data', 'lastName'], '')

  return `${firstName} ${lastName}`
}

/**
 * Get the most recent eligible entitlement (first after filtering out
 * content_set_id === SUBSCRIPTION_CODE_PURCHASE_ONLY and truthy ecommerce_sku).
 * @param {ReduxState} state redux state
 * @returns {Map|undefined} first eligible entitlement Map, or undefined if none
 */
export function selectMostRecentEligibleEntitlement (state) {
  const { user = Map() } = state
  const entitlementsList = user.getIn(['data', 'userEntitlements'], List()) || List()
  const filtered = filterEntitlementsByContentSetAndSku(entitlementsList)
  const first = filtered.get(0)
  return first || undefined
}

/**
 * Get the most recent eligible entitlement from a user Map (e.g. from props).
 * @param {Map} user - user state Map (e.g. state.user or props.user)
 * @returns {Map|undefined} first eligible entitlement Map, or undefined if none
 */
export function selectMostRecentEligibleEntitlementFromUser (user = Map()) {
  if (!Map.isMap(user) || !user.getIn(['data', 'userEntitlements'])) return undefined
  const entitlementsList = user.getIn(['data', 'userEntitlements'], List()) || List()
  const filtered = filterEntitlementsByContentSetAndSku(entitlementsList)
  const first = filtered.get(0)
  return first || undefined
}

/**
 * Get entitlements with a truthy ecommerce_sku from Redux state.
 * @param {ReduxState} state redux state
 * @returns {List} List of entitlement Maps with ecommerce_sku set
 */
export function selectEcommerceSkuEntitlements (state) {
  const { user = Map() } = state
  const entitlementsList = user.getIn(['data', 'userEntitlements'], List()) || List()
  return filterEcommerceSkuEntitlements(entitlementsList)
}

/**
 * Get entitlements with a truthy ecommerce_sku from a user Map (e.g. from props).
 * @param {Map} user - user state Map (e.g. state.user or props.user)
 * @returns {List} List of entitlement Maps with ecommerce_sku set
 */
export function selectEcommerceSkuEntitlementsFromUser (user = Map()) {
  if (!Map.isMap(user)) return List()
  const entitlementsList = user.getIn(['data', 'userEntitlements'], List()) || List()
  return filterEcommerceSkuEntitlements(entitlementsList)
}
